import { useState, useEffect, useCallback } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../services/auth.jsx'
import { api } from '../services/api.js'
import { STATUS_CFG, dateStatus, fmtDate, diffDays, ACTIVITIES_FALLBACK } from '../services/dates.js'
import ProductEditor from '../components/ProductEditor.jsx'
import ActivityModal from '../components/ActivityModal.jsx'
import AreaFormModal from '../components/AreaFormModal.jsx'
import RecommendationModal from '../components/RecommendationModal.jsx'

export default function Dashboard() {
  const { user, logout, currentFazenda, setCurrentFazenda, currentSafra, setCurrentSafra } = useAuth()
  const navigate = useNavigate()
  const isAdmin = user?.role === 'admin'
  // Safra fechada = somente leitura; admin só edita em safra aberta
  const canEdit = isAdmin && !currentSafra?.closed

  const [areas, setAreas] = useState([])
  const [activities, setActivities] = useState(ACTIVITIES_FALLBACK)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filter, setFilter] = useState('all')
  const [showArchived, setShowArchived] = useState(false)

  // Admin: lista de fazendas para o seletor
  const [fazendas, setFazendas] = useState([])

  // Safras da fazenda atual (para o seletor de safra)
  const [safras, setSafras] = useState([])

  // Modals
  const [editingProduct, setEditingProduct] = useState(null)
  const [editingActivity, setEditingActivity] = useState(null)
  const [editingArea, setEditingArea] = useState(null)   // area obj | 'new'
  const [deletingArea, setDeletingArea] = useState(null)
  const [showRecommendation, setShowRecommendation] = useState(false)

  // Carrega lista de fazendas (só admin)
  useEffect(() => {
    if (!isAdmin) return
    api.getFazendas().then(setFazendas).catch(() => {})
  }, [isAdmin])

  // Carrega as safras da fazenda atual e garante uma safra selecionada (a atual)
  useEffect(() => {
    if (!currentFazenda?.id) { setSafras([]); return }
    api.getSafras(currentFazenda.id).then(list => {
      setSafras(list)
      const stillValid = list.find(s => s.id === currentSafra?.id)
      if (!stillValid) {
        const cur = list.find(s => s.is_current) || list[0] || null
        setCurrentSafra(cur ? { id: cur.id, name: cur.name, closed: cur.closed } : null)
      } else if (stillValid.closed !== currentSafra?.closed) {
        // mantém o flag closed em dia (ex.: safra fechada/reaberta em outra aba)
        setCurrentSafra({ id: stillValid.id, name: stillValid.name, closed: stillValid.closed })
      }
    }).catch(() => setSafras([]))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentFazenda?.id])

  const fetchAll = useCallback(async () => {
    if (!currentFazenda?.id) {
      setAreas([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const [areasData, activitiesData] = await Promise.all([
        api.getAreas(currentFazenda.id, showArchived),
        api.getActivities(),
      ])
      setAreas(areasData)
      setActivities(activitiesData.filter(a => a.enabled))
    } catch (e) {
      setError(e.message)
    } finally {
      setLoading(false)
    }
  }, [currentFazenda?.id, currentSafra?.id, showArchived])

  useEffect(() => { fetchAll() }, [fetchAll])

  // Áreas ativas (visão de trabalho) x arquivadas (só quando showArchived)
  const activeAreas = areas.filter(a => a.active !== false)
  const archivedAreas = areas.filter(a => a.active === false)
  // Pastagens ficam numa lista à parte, separadas das demais áreas (lavoura/outro)
  const pastagens = activeAreas.filter(a => a.uso === 'pastagem')
  const workAreas = activeAreas.filter(a => a.uso !== 'pastagem')

  // ── Stats ──────────────────────────────────────────────────────────────────
  const stats = { overdue: 0, soon: 0, upcoming: 0, done: 0, total: 0 }
  workAreas.forEach(area => {
    activities.forEach(act => {
      const plannedDate = area.planned_dates?.find(d => d.activity_key === act.key)?.planned_date ?? null
      if (!plannedDate) return
      stats.total++
      const record = area.activities?.find(r => r.activity_key === act.key)
      const s = dateStatus(plannedDate, record?.done)
      if (s in stats) stats[s]++
    })
  })

  // ── Filtered areas ─────────────────────────────────────────────────────────
  const filtered = workAreas.filter(area => {
    if (filter === 'all') return true
    return activities.some(act => {
      const plannedDate = area.planned_dates?.find(d => d.activity_key === act.key)?.planned_date ?? null
      if (!plannedDate) return false
      const record = area.activities?.find(r => r.activity_key === act.key)
      return dateStatus(plannedDate, record?.done) === filter
    })
  })

  // ── Handlers ──────────────────────────────────────────────────────────────
  async function handleSaveProducts(area, actKey, products) {
    await api.setProducts(area.id, actKey, products, currentFazenda.id)
    await fetchAll()
    setEditingProduct(null)
  }

  async function handleSaveActivity(area, actKey, { done, usedProducts, notes, performedAt, plannedDate }) {
    await api.recordActivity(area.id, actKey, done, usedProducts, notes, performedAt, currentFazenda.id)
    if (plannedDate !== undefined) {
      await api.setPlannedDate(area.id, actKey, plannedDate, currentFazenda.id)
    }
    await fetchAll()
    setEditingActivity(null)
  }

  async function handleSaveArea(data) {
    if (editingArea === 'new') {
      await api.createArea(data, currentFazenda.id)
    } else {
      await api.updateArea(editingArea.id, data, currentFazenda.id)
    }
    await fetchAll()
    setEditingArea(null)
  }

  async function handleDeleteArea(area) {
    await api.deleteArea(area.id, currentFazenda.id)
    await fetchAll()
    setDeletingArea(null)
  }

  async function handleArchiveArea(area) {
    await api.archiveArea(area.id, currentFazenda.id)
    await fetchAll()
  }

  async function handleRestoreArea(area) {
    await api.restoreArea(area.id, currentFazenda.id)
    await fetchAll()
  }

  async function handleReorder(area, direction) {
    const sorted = [...workAreas].sort((a, b) => a.ordem - b.ordem || a.id - b.id)
    const idx = sorted.findIndex(a => a.id === area.id)
    const swapIdx = direction === 'up' ? idx - 1 : idx + 1
    if (swapIdx < 0 || swapIdx >= sorted.length) return
    const ids = sorted.map(a => a.id)
      ;[ids[idx], ids[swapIdx]] = [ids[swapIdx], ids[idx]]
    const items = ids.map((id, i) => ({ id, ordem: i }))
    await api.reorderAreas(items, currentFazenda.id)
    await fetchAll()
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f0fdf4' }}>
      {/* Header */}
      <div style={{ background: '#14532d', padding: '0 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
            <span style={{ fontSize: 26 }}>🌾</span>
            <div>
              <div style={{ fontSize: 10, letterSpacing: '0.18em', color: '#86efac', fontWeight: 700, textTransform: 'uppercase' }}>
                {currentFazenda?.name ?? (isAdmin ? 'Selecione uma fazenda' : '—')}
              </div>
              <div style={{ fontSize: 17, fontWeight: 800, color: '#fff', display: 'flex', alignItems: 'center', gap: 8 }}>
                {currentSafra?.name ? `Planejamento — ${currentSafra.name}` : 'Planejamento'}
                {currentSafra?.closed && (
                  <span style={{ fontSize: 10, fontWeight: 700, background: '#fbbf24', color: '#713f12', borderRadius: 999, padding: '2px 8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    🔒 fechada
                  </span>
                )}
              </div>
            </div>
            {/* Seletor de fazenda (admin apenas) */}
            {isAdmin && fazendas.length > 0 && (
              <select
                value={currentFazenda?.id ?? ''}
                onChange={e => {
                  const id = Number(e.target.value)
                  const f = fazendas.find(f => f.id === id)
                  setCurrentFazenda(f ? { id: f.id, name: f.name } : null)
                }}
                style={{
                  background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: 8, padding: '5px 10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', outline: 'none',
                }}
              >
                <option value="" style={{ background: '#14532d' }}>— escolher fazenda —</option>
                {fazendas.map(f => (
                  <option key={f.id} value={f.id} style={{ background: '#14532d' }}>{f.name}</option>
                ))}
              </select>
            )}
            {/* Seletor de safra */}
            {currentFazenda && safras.length > 0 && (
              <select
                value={currentSafra?.id ?? ''}
                onChange={e => {
                  const id = Number(e.target.value)
                  const s = safras.find(s => s.id === id)
                  setCurrentSafra(s ? { id: s.id, name: s.name, closed: s.closed } : null)
                }}
                title="Safra"
                style={{
                  background: 'rgba(255,255,255,0.12)', color: '#fff', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: 8, padding: '5px 10px', fontSize: 13, fontWeight: 600, cursor: 'pointer', outline: 'none',
                }}
              >
                {safras.map(s => (
                  <option key={s.id} value={s.id} style={{ background: '#14532d' }}>
                    {s.name}{s.is_current ? ' • atual' : ''}{s.closed ? ' 🔒' : ''}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            {isAdmin && (
              <>
                <Link to="/fazendas" style={navLinkStyle}>🏡 Fazendas</Link>
                <Link to="/safras" style={navLinkStyle}>🗓️ Safras</Link>
                <Link to="/activities" style={navLinkStyle}>⚙ Atividades</Link>
                <Link to="/users" style={navLinkStyle}>👥 Usuários</Link>
              </>
            )}
            {(isAdmin || user?.role === 'mecanizacao') && (
              <Link to="/mecanizacao" style={navLinkStyle}>🚜 Mecanização</Link>
            )}
            {(isAdmin || user?.role === 'owner') && (
              <Link to="/insumos" style={navLinkStyle}>🌿 Insumos</Link>
            )}
            <span style={{
              background: 'rgba(255,255,255,0.1)', borderRadius: 999, padding: '5px 14px',
              fontSize: 12, fontWeight: 700, color: isAdmin ? '#fbbf24' : '#86efac',
            }}>
              {isAdmin ? '⚙️ ' : '👷 '}{user?.username}
            </span>
            <button onClick={() => { logout(); navigate('/login') }} style={{
              background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.2)',
              color: '#fff', borderRadius: 999, padding: '5px 14px', cursor: 'pointer', fontSize: 12, fontWeight: 600,
            }}>Sair</button>
          </div>
        </div>
      </div>

      {/* Aviso para admin sem fazenda selecionada */}
      {isAdmin && !currentFazenda && (
        <div style={{ margin: '48px auto', maxWidth: 480, textAlign: 'center', padding: '40px 32px', background: '#fff', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>🏡</div>
          <div style={{ fontSize: 18, fontWeight: 800, color: '#14532d', marginBottom: 8 }}>Selecione uma fazenda</div>
          <div style={{ fontSize: 14, color: '#64748b', marginBottom: 24 }}>
            Escolha uma fazenda no seletor do header para ver o planejamento.
          </div>
          <Link to="/fazendas" style={{ background: '#16a34a', color: '#fff', padding: '10px 24px', borderRadius: 10, fontWeight: 700, fontSize: 14, textDecoration: 'none' }}>
            Gerenciar Fazendas
          </Link>
        </div>
      )}

      {currentFazenda && (
        <>
          {/* Stats bar */}
          <div style={{ background: '#052e16', padding: '10px 24px', display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { key: 'all', icon: '📋', label: 'Total', value: stats.total, color: '#86efac' },
              { key: 'done', icon: '✅', label: 'Realizadas', value: stats.done, color: '#4ade80' },
              { key: 'overdue', icon: '🔴', label: 'Atrasadas', value: stats.overdue, color: '#f87171' },
              { key: 'soon', icon: '🟡', label: '≤15 dias', value: stats.soon, color: '#fbbf24' },
              { key: 'upcoming', icon: '🟠', label: '≤30 dias', value: stats.upcoming, color: '#fb923c' },
            ].map(s => (
              <button key={s.key} onClick={() => setFilter(s.key)} style={{
                background: filter === s.key ? 'rgba(255,255,255,0.12)' : 'transparent',
                border: filter === s.key ? '1.5px solid rgba(255,255,255,0.3)' : '1.5px solid transparent',
                borderRadius: 10, padding: '7px 14px', cursor: 'pointer',
                display: 'flex', alignItems: 'center', gap: 8,
              }}>
                <span style={{ fontSize: 18, fontWeight: 800, color: s.color, fontFamily: "'DM Mono', monospace" }}>{s.value}</span>
                <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.55)', fontWeight: 600, lineHeight: 1.3, textAlign: 'left' }}>
                  {s.icon}<br />{s.label}
                </span>
              </button>
            ))}
            <div style={{ marginLeft: 'auto', fontFamily: "'DM Mono', monospace", fontSize: 11, color: '#4ade80', fontWeight: 600 }}>
              {new Date().toLocaleDateString('pt-BR', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
            </div>
          </div>

          {error && (
            <div style={{ margin: '16px 24px', padding: '12px 16px', background: '#fee2e2', color: '#7f1d1d', borderRadius: 10, fontWeight: 600, fontSize: 13 }}>
              Erro: {error}
            </div>
          )}

          {canEdit && (
            <div style={{ margin: '16px 24px 0', display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              <div style={{ padding: '10px 16px', background: '#fef9c3', border: '1.5px solid #fde047', borderRadius: 10, fontSize: 13, color: '#713f12', fontWeight: 600 }}>
                ⚙️ Modo admin — clique em qualquer célula para registrar/editar data prevista e realizada. Use ✏️ para editar a área.
              </div>
              <button onClick={() => setEditingArea('new')} style={{
                background: '#16a34a', color: '#fff', border: 'none', borderRadius: 10,
                padding: '10px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 13,
              }}>
                ➕ Nova Área
              </button>
              <button onClick={() => setShowRecommendation(true)} style={{
                background: '#0369a1', color: '#fff', border: 'none', borderRadius: 10,
                padding: '10px 18px', cursor: 'pointer', fontWeight: 700, fontSize: 13,
              }}>
                📋 Recomendação
              </button>
            </div>
          )}

          {isAdmin && currentSafra?.closed && (
            <div style={{ margin: '16px 24px 0' }}>
              <div style={{ padding: '10px 16px', background: '#fee2e2', border: '1.5px solid #fca5a5', borderRadius: 10, fontSize: 13, color: '#7f1d1d', fontWeight: 600 }}>
                🔒 Safra fechada (histórico, somente leitura). Para editar, defina-a como aberta na tela de Safras.
              </div>
            </div>
          )}

          {isAdmin && (
            <div style={{ margin: '16px 24px 0', display: 'flex', alignItems: 'center', gap: 10 }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: '#475569', fontWeight: 600 }}>
                <input type="checkbox" checked={showArchived} onChange={e => setShowArchived(e.target.checked)} style={{ width: 16, height: 16, cursor: 'pointer' }} />
                📥 Mostrar áreas arquivadas
              </label>
              {showArchived && (
                <span style={{ fontSize: 12, color: '#94a3b8' }}>
                  {archivedAreas.length} arquivada{archivedAreas.length !== 1 ? 's' : ''}
                </span>
              )}
            </div>
          )}

          {/* Table */}
          {loading ? (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 60, color: '#64748b', fontSize: 16 }}>Carregando...</div>
          ) : (
            <div style={{ padding: '20px 16px', overflowX: 'auto' }}>
              <div style={{ overflowX: 'auto', borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', background: '#fff' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#14532d' }}>
                      {canEdit && <th style={thStyle('center')} title="Reordenar">↕</th>}
                      <th style={thStyle()}>Lavoura</th>
                      <th style={thStyle('right')}>Área</th>
                      {activities.map(a => (
                        <th key={a.key} style={{ ...thStyle('center'), color: '#86efac', minWidth: 130 }}>{a.label}</th>
                      ))}
                      <th style={thStyle()}>Cultura Inv.</th>
                      <th style={thStyle()}>Opção Inv.</th>
                      <th style={thStyle()}>Opção Milho</th>
                      {canEdit && <th style={thStyle('center')}>Ações</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.length === 0 && (
                      <tr>
                        <td colSpan={canEdit ? activities.length + 7 : activities.length + 5}
                          style={{ padding: 40, textAlign: 'center', color: '#94a3b8', fontSize: 14 }}>
                          Nenhuma área neste filtro.
                        </td>
                      </tr>
                    )}
                    {filtered.map((area, i) => (
                      <AreaRow
                        key={area.id}
                        area={area}
                        even={i % 2 === 0}
                        isAdmin={canEdit}
                        activities={activities}
                        isFirst={i === 0}
                        isLast={i === filtered.length - 1}
                        onEditProducts={(actKey) => setEditingProduct({ area, actKey })}
                        onEditActivity={(actKey, label) => setEditingActivity({ area, actKey, label })}
                        onEditArea={() => setEditingArea(area)}
                        onArchiveArea={() => handleArchiveArea(area)}
                        onReorder={(dir) => handleReorder(area, dir)}
                      />
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ background: '#f0fdf4', borderTop: '2px solid #86efac' }}>
                      {canEdit && <td />}
                      <td style={{ padding: '12px 16px', fontWeight: 800, color: '#14532d', fontSize: 14 }}>TOTAL</td>
                      <td style={{ padding: '12px 10px', textAlign: 'right', fontWeight: 800, color: '#14532d', fontFamily: "'DM Mono', monospace" }}>
                        {filtered.reduce((s, a) => s + a.ha, 0).toFixed(2)} ha
                      </td>
                      <td colSpan={activities.length + 3} />
                      {canEdit && <td />}
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Pastagens — lista à parte, separada das lavouras */}
              <div style={{ marginTop: 20, borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', background: '#fff', overflow: 'hidden' }}>
                <div style={{ padding: '12px 20px', background: '#3f6212', color: '#ecfccb', fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span>🐄 Pastagens ({pastagens.length})</span>
                  <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: '#bef264' }}>
                    {pastagens.reduce((s, a) => s + a.ha, 0).toFixed(2)} ha
                  </span>
                </div>
                {pastagens.length === 0 ? (
                  <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                    Nenhuma pastagem nesta safra. Marque o tipo de uso de uma área como <strong>Pastagem</strong> para listá-la aqui.
                  </div>
                ) : (
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                    <tbody>
                      {pastagens.map((area, i) => (
                        <tr key={area.id} style={{ background: i % 2 === 0 ? '#f7fee7' : '#fff' }}>
                          <td style={{ ...tdStyle(), color: '#3f6212', fontWeight: 700 }}>{area.name}</td>
                          <td style={{ ...tdStyle(), textAlign: 'right', fontFamily: "'DM Mono', monospace", color: '#4d7c0f' }}>{area.ha} ha</td>
                          <td style={{ ...tdStyle(), textAlign: 'right', whiteSpace: 'nowrap' }}>
                            {canEdit && (
                              <>
                                <button onClick={() => setEditingArea(area)} style={iconBtnStyle('#dbeafe', '#1d4ed8')} title="Editar área">✏️</button>
                                <button onClick={() => handleArchiveArea(area)} style={{ ...iconBtnStyle('#fef3c7', '#b45309'), marginLeft: 4 }} title="Arquivar área (oculta, mantém o histórico)">📥</button>
                              </>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {showArchived && (
                <div style={{ marginTop: 20, borderRadius: 16, boxShadow: '0 4px 24px rgba(0,0,0,0.07)', background: '#fff', overflow: 'hidden' }}>
                  <div style={{ padding: '12px 20px', background: '#78350f', color: '#fde68a', fontWeight: 700, fontSize: 13 }}>
                    📥 Áreas arquivadas ({archivedAreas.length})
                  </div>
                  {archivedAreas.length === 0 ? (
                    <div style={{ padding: 24, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                      Nenhuma área arquivada nesta safra.
                    </div>
                  ) : (
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                      <tbody>
                        {archivedAreas.map((area, i) => (
                          <tr key={area.id} style={{ background: i % 2 === 0 ? '#fffbeb' : '#fff' }}>
                            <td style={{ ...tdStyle(), color: '#92400e', fontWeight: 700 }}>{area.name}</td>
                            <td style={{ ...tdStyle(), textAlign: 'right', fontFamily: "'DM Mono', monospace", color: '#a16207' }}>{area.ha} ha</td>
                            <td style={{ ...tdStyle(), textAlign: 'right', whiteSpace: 'nowrap' }}>
                              {canEdit && (
                                <>
                                  <button onClick={() => handleRestoreArea(area)} style={iconBtnStyle('#dcfce7', '#15803d')} title="Restaurar área">♻️</button>
                                  <button onClick={() => setDeletingArea(area)} style={{ ...iconBtnStyle('#fee2e2', '#b91c1c'), marginLeft: 4 }} title="Excluir permanentemente">🗑</button>
                                </>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {editingProduct && (
        <ProductEditor
          area={editingProduct.area}
          actKey={editingProduct.actKey}
          currentProducts={
            editingProduct.area.available_products
              .filter(p => p.activity_key === editingProduct.actKey)
              .map(p => ({ product_name: p.product_name, dose: p.dose, unit: p.unit }))
          }
          onSave={(products) => handleSaveProducts(editingProduct.area, editingProduct.actKey, products)}
          onClose={() => setEditingProduct(null)}
        />
      )}
      {editingActivity && (
        <ActivityModal
          area={editingActivity.area}
          actKey={editingActivity.actKey}
          actLabel={editingActivity.label}
          plannedDate={editingActivity.area.planned_dates?.find(d => d.activity_key === editingActivity.actKey)?.planned_date ?? null}
          currentRecord={editingActivity.area.activities?.find(r => r.activity_key === editingActivity.actKey)}
          availableProducts={
            editingActivity.area.available_products
              .filter(p => p.activity_key === editingActivity.actKey)
              .map(p => p.product_name)
          }
          isAdmin={isAdmin}
          onSave={(data) => handleSaveActivity(editingActivity.area, editingActivity.actKey, data)}
          onClose={() => setEditingActivity(null)}
        />
      )}
      {editingArea && (
        <AreaFormModal
          area={editingArea === 'new' ? null : editingArea}
          onSave={handleSaveArea}
          onClose={() => setEditingArea(null)}
        />
      )}
      {deletingArea && (
        <DeleteConfirmModal
          area={deletingArea}
          onConfirm={() => handleDeleteArea(deletingArea)}
          onClose={() => setDeletingArea(null)}
        />
      )}
      {showRecommendation && currentFazenda && (
        <RecommendationModal
          areas={workAreas}
          activities={activities}
          fazendaId={currentFazenda.id}
          onClose={() => setShowRecommendation(false)}
          onApplied={fetchAll}
        />
      )}
    </div>
  )
}

// ── AreaRow ───────────────────────────────────────────────────────────────────
function AreaRow({ area, even, isAdmin, activities, isFirst, isLast, onEditProducts, onEditActivity, onEditArea, onArchiveArea, onReorder }) {
  const bg = even ? '#f8fdf9' : '#fff'
  return (
    <tr style={{ background: bg }}>
      {isAdmin && (
        <td style={{ ...tdStyle(), textAlign: 'center', padding: '4px 6px' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'center' }}>
            <button onClick={() => onReorder('up')} disabled={isFirst} style={reorderBtnStyle(isFirst)} title="Mover para cima">▲</button>
            <button onClick={() => onReorder('down')} disabled={isLast} style={reorderBtnStyle(isLast)} title="Mover para baixo">▼</button>
          </div>
        </td>
      )}
      <td style={tdStyle()}><strong style={{ color: '#14532d' }}>{area.name}</strong></td>
      <td style={{ ...tdStyle(), textAlign: 'right', fontFamily: "'DM Mono', monospace", color: '#64748b' }}>
        {area.ha} ha
      </td>
      {activities.map(act => {
        const plannedDate = area.planned_dates?.find(d => d.activity_key === act.key)?.planned_date ?? null
        const record = area.activities?.find(r => r.activity_key === act.key)
        const displayDate = plannedDate || record?.performed_at || null
        const prods = area.available_products?.filter(p => p.activity_key === act.key).map(p => p.product_name) ?? []
        const usedProds = record?.used_products?.map(p => p.product_name) ?? []
        const status = dateStatus(plannedDate, record?.done)
        const cfg = STATUS_CFG[status]
        const diff = plannedDate && !record?.done ? diffDays(plannedDate) : null

        return (
          <td key={act.key} style={{ ...tdStyle(), verticalAlign: 'top', minWidth: 130 }}>
            {displayDate ? (
              <button
                onClick={() => onEditActivity(act.key, act.label)}
                style={{
                  width: '100%', background: cfg.bg, color: cfg.text,
                  border: `1.5px solid ${cfg.border}`,
                  borderRadius: 8, padding: '6px 8px',
                  cursor: 'pointer', textAlign: 'left', marginBottom: 4,
                }}
              >
                <div style={{ fontFamily: "'DM Mono', monospace", fontSize: 11, fontWeight: 600 }}>{fmtDate(displayDate)}</div>
                {diff !== null && (
                  <div style={{ fontSize: 10, opacity: 0.7 }}>
                    {diff === 0 ? 'hoje' : diff > 0 ? `em ${diff}d` : `${Math.abs(diff)}d atrás`}
                  </div>
                )}
                {record?.done && <div style={{ fontSize: 10, fontWeight: 700, color: '#065f46' }}>✓ realizado</div>}
                {!plannedDate && record?.performed_at && <div style={{ fontSize: 10, color: '#64748b' }}>data realizada</div>}
              </button>
            ) : (
              isAdmin ? (
                <button
                  onClick={() => onEditActivity(act.key, act.label)}
                  title="Registrar atividade"
                  style={{
                    width: '100%', height: 44, background: '#f0fdf4',
                    borderRadius: 8, border: '1.5px dashed #86efac', marginBottom: 4,
                    cursor: 'pointer', color: '#86efac', fontSize: 18,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >+</button>
              ) : (
                <button
                  onClick={() => onEditActivity(act.key, act.label)}
                  title="Registrar atividade"
                  style={{
                    width: '100%', height: 44, background: '#f8fafc',
                    borderRadius: 8, border: '1.5px dashed #e2e8f0', marginBottom: 4,
                    cursor: 'pointer', color: '#94a3b8', fontSize: 12, fontWeight: 600,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}
                >registrar</button>
              )
            )}

            {isAdmin ? (
              <button onClick={() => onEditProducts(act.key)} style={{
                fontSize: 10, padding: '2px 8px', borderRadius: 999,
                border: `1.5px dashed ${prods.length ? '#86efac' : '#e2e8f0'}`,
                background: prods.length ? '#f0fdf4' : 'transparent',
                color: prods.length ? '#15803d' : '#94a3b8',
                cursor: 'pointer', fontWeight: 600,
              }}>
                {prods.length ? `${prods.length} produto${prods.length > 1 ? 's' : ''} ✏️` : '+ produtos'}
              </button>
            ) : (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {prods.map(p => (
                  <span key={p} style={{
                    fontSize: 10, padding: '1px 6px', borderRadius: 999, fontWeight: 600,
                    background: usedProds.includes(p) ? '#dcfce7' : '#f1f5f9',
                    color: usedProds.includes(p) ? '#15803d' : '#64748b',
                  }}>{p}</span>
                ))}
              </div>
            )}
          </td>
        )
      })}
      <td style={{ ...tdStyle(), color: '#64748b' }}>{area.cultura_inverno ?? '—'}</td>
      <td style={{ ...tdStyle(), color: '#64748b', fontSize: 11 }}>{area.opcao_inverno ?? '—'}</td>
      <td style={{ ...tdStyle(), color: '#64748b' }}>{area.opcao_milho ?? '—'}</td>
      {isAdmin && (
        <td style={{ ...tdStyle(), textAlign: 'center', whiteSpace: 'nowrap' }}>
          <button onClick={onEditArea} style={iconBtnStyle('#dbeafe', '#1d4ed8')} title="Editar área">✏️</button>
          <button onClick={onArchiveArea} style={{ ...iconBtnStyle('#fef3c7', '#b45309'), marginLeft: 4 }} title="Arquivar área (oculta, mantém o histórico)">📥</button>
        </td>
      )}
    </tr>
  )
}

// ── DeleteConfirmModal ────────────────────────────────────────────────────────
function DeleteConfirmModal({ area, onConfirm, onClose }) {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function confirm() {
    setLoading(true)
    try { await onConfirm() } catch (e) { setError(e.message); setLoading(false) }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={onClose}>
      <div style={{ background: '#fff', borderRadius: 16, padding: '28px 32px', maxWidth: 380, width: '100%', boxShadow: '0 8px 40px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
        <div style={{ fontSize: 32, marginBottom: 12, textAlign: 'center' }}>⚠️</div>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#0f172a', marginBottom: 8, textAlign: 'center' }}>Excluir área?</div>
        <div style={{ fontSize: 14, color: '#475569', textAlign: 'center', marginBottom: 20 }}>
          A área <strong>"{area.name}"</strong> e todos os seus registros serão excluídos permanentemente.
        </div>
        {error && <div style={{ background: '#fee2e2', color: '#7f1d1d', padding: '8px 12px', borderRadius: 8, fontSize: 13, marginBottom: 12 }}>{error}</div>}
        <div style={{ display: 'flex', gap: 10 }}>
          <button onClick={onClose} disabled={loading} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: '1.5px solid #e2e8f0', background: '#fff', color: '#0f172a', cursor: 'pointer', fontWeight: 600, fontSize: 14 }}>Cancelar</button>
          <button onClick={confirm} disabled={loading} style={{ flex: 1, padding: '10px 0', borderRadius: 8, border: 'none', background: '#dc2626', color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
            {loading ? 'Excluindo...' : 'Excluir'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Style helpers ─────────────────────────────────────────────────────────────
function thStyle(align = 'left') {
  return { padding: '13px 12px', textAlign: align, color: '#fff', fontWeight: 700, fontSize: 11, letterSpacing: '0.04em', whiteSpace: 'nowrap' }
}
function tdStyle() {
  return { padding: '8px 12px', borderBottom: '1px solid #e7f3ea', verticalAlign: 'middle' }
}
function reorderBtnStyle(disabled) {
  return {
    background: 'transparent', border: '1px solid #e2e8f0', borderRadius: 4,
    cursor: disabled ? 'default' : 'pointer', fontSize: 10,
    color: disabled ? '#cbd5e1' : '#64748b', padding: '1px 5px', lineHeight: 1,
  }
}
function iconBtnStyle(bg, color) {
  return { background: bg, border: 'none', borderRadius: 6, padding: '4px 7px', cursor: 'pointer', fontSize: 13, color, lineHeight: 1 }
}
const navLinkStyle = {
  color: 'rgba(255,255,255,0.8)', textDecoration: 'none', fontSize: 12,
  fontWeight: 600, padding: '5px 12px', borderRadius: 999,
  border: '1px solid rgba(255,255,255,0.2)', background: 'rgba(255,255,255,0.06)',
}
